import { supabase } from '@/lib/supabase';

const normalizeEntryMode = (document = {}, type) => {
  if (type !== 'entrada') return null;
  return document.entry_mode || 'normal';
};

export const generateDocumentNumber = async (type) => {
  try {
    // Obtener todos los números existentes de este tipo (paginado) y calcular el máximo sufijo numérico
    const PAGE_SIZE = 1000;
    let offset = 0;
    let lastNumber = 0;

    const extractNumericSuffix = (raw) => {
      if (!raw) return NaN;
      const str = String(raw);
      // Tomar la última secuencia de dígitos en la cadena (funciona para SAL-<ts>, sal-000001, 000001, etc.)
      const match = str.match(/(\d+)(?!.*\d)/);
      if (match) {
        const parsed = parseInt(match[1]);
        return Number.isNaN(parsed) ? NaN : parsed;
      }
      const fallback = parseInt(str);
      return Number.isNaN(fallback) ? NaN : fallback;
    };

    /* eslint-disable no-constant-condition */
    while (true) {
      const { data: page, error } = await supabase
        .from('documents')
        .select('number')
        .eq('type', type)
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      for (const row of (page || [])) {
        const num = extractNumericSuffix(row.number);
        if (Number.isFinite(num) && num > lastNumber) lastNumber = num;
      }
      if (!page || page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Para salidas, usar el formato "sal-000001"
    if (type === 'salida') {
      return `SAL-${(lastNumber + 1).toString().padStart(6, '0')}`;
    }

    // Para entradas, mantener el formato actual
    return (lastNumber + 1).toString().padStart(6, '0');
  } catch (error) {
    console.error('Error generating document number:', error);
    throw error;
  }
};

export const saveDocument = async (document, type) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No hay sesión activa');

    const timestamp = new Date().toISOString();
    const entryMode = normalizeEntryMode(document, type);
    


    // OPTIMIZACIÓN 1: Obtener todo el inventario necesario en una sola consulta
    const inventoryKeys = document.items.map(item => ({
      product_id: item.product_id,
      size: item.size,
      color: item.color
    }));

    // Obtener inventario actual en lote
    const { data: currentInventory, error: inventoryError } = await supabase
      .from('inventory')
      .select('product_id, size, color, quantity')
      .in('product_id', [...new Set(inventoryKeys.map(k => k.product_id))]);

    if (inventoryError) throw inventoryError;

    // Crear mapa de inventario para acceso rápido
    const inventoryMap = new Map();
    currentInventory?.forEach(inv => {
      const key = `${inv.product_id}-${inv.size}-${inv.color}`;
      inventoryMap.set(key, inv.quantity || 0);
    });

    // OPTIMIZACIÓN 2: Verificar stock en lote para salidas
    if (type === 'salida') {
      let previousItemsMap = new Map();
      
      // Si es edición, obtener items anteriores en una sola consulta
      if (document.id) {
        const { data: previousItems, error: prevError } = await supabase
          .from('document_items')
          .select('product_id, size, color, quantity')
          .eq('document_id', document.id);

        if (prevError) throw prevError;

        previousItems?.forEach(item => {
          const key = `${item.product_id}-${item.size}-${item.color}`;
          previousItemsMap.set(key, item.quantity || 0);
        });
      }

      // Verificar stock para todos los items
      for (const item of document.items) {
        const key = `${item.product_id}-${item.size}-${item.color}`;
        const currentStock = inventoryMap.get(key) || 0;
        const previousQuantity = previousItemsMap.get(key) || 0;
        const adjustedStock = currentStock + previousQuantity;

        if (adjustedStock < item.quantity) {
          throw new Error(`Stock insuficiente para ${item.product_code} - Talla: ${item.size}, Color: ${item.color}. Stock disponible: ${adjustedStock}`);
        }
      }
    }

    // Validar datos antes de enviar
    if (!document.items || document.items.length === 0) {
      throw new Error('El documento debe tener al menos un item');
    }

    // Preparar parámetros para la función RPC con validación estricta
    const rpcParams = {
      p_document_id: (document.id && document.id.trim() !== '') ? document.id : null,
      p_type: type,
      p_number: document.number,
      p_date: document.date,
      p_customer_id: (document.customerId && document.customerId.trim() !== '') ? document.customerId : null,
      p_user_id: (session.user.id && session.user.id.trim() !== '') ? session.user.id : null,
      p_items: document.items.map(item => ({
        product_id: item.product_id,
        size: item.size,
        color: item.color,
        quantity: item.quantity,
        price: item.price,
        theoretical_quantity: Number.isFinite(Number(item.theoretical_quantity)) ? Number(item.theoretical_quantity) : null,
        counted_quantity: Number.isFinite(Number(item.counted_quantity)) ? Number(item.counted_quantity) : null
      })),
      p_timestamp: timestamp,
      p_regularization_reason: document.regularization_reason || null,
      p_entry_mode: entryMode
    };

    const { data: rpcResult, error: rpcError } = await supabase.rpc('save_document_transaction', rpcParams);
    if (rpcError) throw rpcError;

    return rpcResult;
  } catch (error) {
    throw error;
  }
};

export async function fetchDocuments(type) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No hay sesión activa');

    // Obtener el rol del usuario
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role')
      .eq('id', session.user.id)
      .single();

    if (userError) throw userError;

    // OPTIMIZACIÓN: Consultas diferentes según el tipo de documento
    let query;
    
    if (type === 'entrada') {
      // Para entradas: consulta más ligera sin joins pesados
      query = supabase
        .from('documents')
        .select(`
          id,
          number,
          date,
          type,
          status,
          total,
          created_at,
          updated_at,
          user_id,
          entry_mode,
          regularization_reason,
          customer:customers(
            id,
            name
          )
        `)
        .eq('type', type)
        .order('date', { ascending: false });
    } else {
      // Para salidas: consulta optimizada con items y productos (sin imágenes para la lista)
      query = supabase
        .from('documents')
        .select(`
          id,
          number,
          date,
          type,
          status,
          total,
          created_at,
          updated_at,
          user_id,
          entry_mode,
          regularization_reason,
          customer:customers(
            id,
            name
          ),
          document_items(
            id,
            product_id,
            size,
            color,
            quantity,
            price,
            product:products(
              id,
              code,
              description
            )
          )
        `)
        .eq('type', type)
        .order('date', { ascending: false });
    }

    // Si el usuario es vendedor, solo mostrar sus documentos
    if (userData.role === 'vendedor') {
      query = query.eq('user_id', session.user.id);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Transformar los datos según el tipo
    const transformedData = data.map(doc => {
      if (type === 'entrada') {
        // Para entradas: estructura simple
        return {
          ...doc,
          entry_mode: doc.entry_mode || 'normal',
          customerId: doc.customer?.id,
          customer: doc.customer,
          items: [] // Las entradas no necesitan items en la lista principal
        };
      } else {
        // Para salidas: estructura completa
        return {
          ...doc,
          entry_mode: doc.entry_mode || null,
          customerId: doc.customer?.id,
          customer: doc.customer,
          items: doc.document_items.map(item => ({
            ...item,
            product: item.product
          }))
        };
      }
    });

    return transformedData;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
}

export async function deleteDocument(documentId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No hay sesión activa');

    // Primero obtenemos los items del documento
    const { data: documentItems, error: fetchError } = await supabase
      .from('document_items')
      .select('product_id, size, color, quantity')
      .eq('document_id', documentId);

    if (fetchError) throw fetchError;

    // Obtenemos el tipo de documento
    const { data: document, error: docError } = await supabase
      .from('documents')
      .select('type')
      .eq('id', documentId)
      .single();

    if (docError) throw docError;

    // Actualizamos el inventario
    for (const item of documentItems || []) {
      const { data: existing, error: inventoryError } = await supabase
        .from('inventory')
        .select('quantity')
        .eq('product_id', item.product_id)
        .eq('size', item.size)
        .eq('color', item.color)
        .maybeSingle();

      if (inventoryError && inventoryError.code !== 'PGRST116') throw inventoryError;

      const currentQuantity = existing?.quantity || 0;
      const adjustment = document.type === 'entrada' ? -item.quantity : item.quantity;
      const newQuantity = currentQuantity + adjustment;

      const { error: updateError } = await supabase
        .from('inventory')
        .upsert([{
          product_id: item.product_id,
          size: item.size,
          color: item.color,
          quantity: newQuantity,
          created_at: new Date().toISOString()
        }], { onConflict: ['product_id', 'size', 'color'] });

      if (updateError) throw updateError;
    }

    // Eliminamos los items del documento
    const { error: itemsError } = await supabase
      .from('document_items')
      .delete()
      .eq('document_id', documentId);

    if (itemsError) throw itemsError;

    // Eliminamos el documento
    const { error: deleteError } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId);

    if (deleteError) throw deleteError;

    return true;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
}
